import classNames from 'classnames';
import { ChevronLeft, Download, Plus, Upload } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useMediaManagerPresetsQuery } from 'modules/manager/store/media-manager-api';
import ImportConflictsModal from 'modules/manager/ui/components/import-conflicts-modal';
import ImportTemplatesResultModal from 'modules/manager/ui/components/import-templates-result-modal';
import MediaManagerPresetCard from 'modules/manager/ui/components/media-manager-preset-card';
import { PRESET_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { PresetGridSkeleton } from 'modules/manager/ui/components/skeletons';
import { useExportTemplates } from 'modules/manager/ui/hooks/use-export-templates';
import { useImportTemplates } from 'modules/manager/ui/hooks/use-import-templates';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deriveAsyncState } from 'shared/utils/async-state';
import { useIsAdmin } from 'shared/hooks/use-is-admin';
import { UDButton, UDText } from 'ud-ui/components';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import useCreateTemplateModal from '../hooks/modals/use-create-template-modal';
import useDeleteTemplateModal from '../hooks/modals/use-delete-template-modal';
import useDuplicateTemplateModal from '../hooks/modals/use-duplicate-template-modal';
import useEditTemplateModal from '../hooks/modals/use-edit-template-modal';

const MediaManagerPresetsPage: React.FC = () => {
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const { data: presets = [], currentData, isFetching, isError, refetch } = useMediaManagerPresetsQuery();
  const presetsState = deriveAsyncState({
    itemsCount: presets.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });

  const breadcrumbs = useMemo(
    () => [
      { label: 'Медиа менеджер', to: Pages.MEDIA_MANAGER.INDEX },
      { label: 'Общие шаблоны', to: Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY },
    ],
    [],
  );

  useEffect(() => {
    setHeaderContent(<UDBreadcrumbs breadcrumbs={breadcrumbs} />);
    return () => setHeaderContent(null);
  }, [breadcrumbs, setHeaderContent]);

  const loadPresets = useCallback(() => {
    void refetch();
  }, [refetch]);

  const openTemplate = useCallback(
    (template: MediaManagerTemplate) => navigate(Pages.MEDIA_MANAGER.builders.editor(template.id)),
    [navigate],
  );

  // A fresh copy lands in the tournament list — go there and let the page flash
  // the new row, so the user sees where it went.
  const onDuplicated = useCallback(
    (copy: MediaManagerTemplate) => {
      navigate(Pages.MEDIA_MANAGER.INDEX, { state: { highlightTemplateId: copy.id } });
    },
    [navigate],
  );

  const { modal: duplicateModal, open: openDuplicateModal, canDuplicate, isDuplicating } = useDuplicateTemplateModal({ onDuplicated });

  const existingNames = useMemo(() => presets.map((t) => t.name.trim()), [presets]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateModal({
    tournamentId: tournamentId ?? null,
    existingNames,
    templatesCount: presets.length,
    onCreated: loadPresets,
    createAsPreset: true,
  });
  const { modal: editModal, open: openEditModal } = useEditTemplateModal({
    templates: presets,
    onUpdated: loadPresets,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateModal({
    onDeleted: loadPresets,
  });

  // Presets arrive with `objects`, so exporting them never reads a template again.
  const { exportTemplates, isExporting } = useExportTemplates();
  const runExport = useCallback(
    (list: readonly MediaManagerTemplate[]) => {
      exportTemplates(list, { label: 'presets' }).catch((error) => {
        console.error('Failed to export media manager presets:', error);
        toast.error('Не удалось выгрузить шаблоны, попробуйте ещё раз');
      });
    },
    [exportTemplates],
  );
  const exportOne = useCallback((template: MediaManagerTemplate) => runExport([template]), [runExport]);

  // Writing presets needs ROLE_ADMIN — nobody else gets a target, and the button is hidden too.
  const {
    pickFile,
    fileInput,
    isImporting,
    result: importResult,
    clearResult: clearImportResult,
    pending: pendingImport,
    confirmOverwrite,
    cancelImport,
  } = useImportTemplates({ target: isAdmin ? 'preset' : null, existing: presets, templatesCount: presets.length });

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex items-center justify-between">
        <UDButton variant="secondary" className="flex items-center" onClick={() => navigate(Pages.MEDIA_MANAGER.INDEX)}>
          <ChevronLeft size={16} className="mr-2" />К шаблонам турнира
        </UDButton>
        <div className="flex items-center gap-3">
          <UDButton
            variant="secondary"
            className="flex items-center"
            disabled={presets.length === 0 || isExporting}
            loading={isExporting}
            onClick={() => runExport(presets)}
          >
            <Download size={16} className="mr-2" />
            Скачать все пресеты
          </UDButton>
          {isAdmin && (
            <UDButton
              variant="secondary"
              className="flex items-center"
              disabled={isImporting}
              loading={isImporting}
              onClick={pickFile}
            >
              <Upload size={16} className="mr-2" />
              Загрузить пресеты
            </UDButton>
          )}
          {isAdmin && (
            <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
              <Plus size={16} className="mr-2" />
              Создать пресет
            </UDButton>
          )}
        </div>
      </div>

      {presetsState.status === 'loading' ? (
        <PresetGridSkeleton isAdmin={isAdmin} canDuplicate={canDuplicate} />
      ) : presetsState.status === 'error' ? (
        <div className="flex h-full flex-col items-center justify-center gap-2" role="alert">
          <UDText variant="body-2">Не удалось загрузить общие шаблоны</UDText>
          <UDButton variant="secondary" onClick={loadPresets}>
            Повторить
          </UDButton>
        </div>
      ) : presetsState.status === 'empty' ? (
        <div className="flex h-full flex-col items-center justify-center gap-2">
          <UDText variant="body-2" className="text-gray-500">
            Пока нет общих шаблонов
          </UDText>
          {isAdmin && (
            <UDButton variant="primary" onClick={openCreateModal}>
              <Plus size={16} className="mr-2" /> Создать пресет
            </UDButton>
          )}
        </div>
      ) : (
        // Обновление после создания / удаления не прячет сетку: она приглушается, как у `UDAsyncContent`.
        <div
          className={classNames(PRESET_GRID_CLASS, 'transition-opacity', {
            'pointer-events-none opacity-50': presetsState.isRefreshing,
          })}
          aria-busy={presetsState.isRefreshing}
        >
          {presets.map((template) => (
            <MediaManagerPresetCard
              key={template.id}
              template={template}
              isAdmin={isAdmin}
              canDuplicate={canDuplicate}
              isDuplicating={isDuplicating}
              onOpen={openTemplate}
              onDuplicate={openDuplicateModal}
              onEdit={openEditModal}
              onDelete={openDeleteModal}
              onExport={exportOne}
              isExporting={isExporting}
            />
          ))}
        </div>
      )}

      {createModal}
      {editModal}
      {deleteModal}
      {duplicateModal}
      {fileInput}
      {pendingImport && <ImportConflictsModal pending={pendingImport} onConfirm={confirmOverwrite} onCancel={cancelImport} />}
      {importResult && <ImportTemplatesResultModal result={importResult} onClose={clearImportResult} />}
    </div>
  );
};

export default MediaManagerPresetsPage;
